import React from 'react';
import type { WeeklyAvailability } from "@applicant-reviewer/api/availability";
import { SLOT_STATUS, DAYS_OF_WEEK } from "@applicant-reviewer/api/availability";
import CompanyLogo from '../../../../components/ui/CompanyLogo';

interface ScheduleSummaryPageProps {
  availability: WeeklyAvailability;
  hourlyRate: number;
  companyName: string;
  companyLogo: string;
  location: string;
  onWatchVideo: () => void;
  onBack: () => void;
  onConfirm: () => void;
}

// Convert slot index to time string (e.g., "8:00 AM")
function slotIndexToTimeString(slotIndex: number): string {
  const totalMinutes = slotIndex * 30; // Each slot is 30 minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

// Group consecutive slots into time ranges
interface TimeBlock {
  startSlot: number;
  endSlot: number;
  status: number;
}

function getTimeBlocks(slots: { index: number; status: number }[]): TimeBlock[] {
  const blocks: TimeBlock[] = [];
  let currentBlock: TimeBlock | null = null;

  for (const slot of slots) {
    // Skip unavailable/closed slots
    if (slot.status === SLOT_STATUS.UNAVAILABLE) {
      if (currentBlock) {
        blocks.push(currentBlock);
        currentBlock = null;
      }
      continue;
    }

    // Start a new block or continue the current one
    if (!currentBlock) {
      currentBlock = {
        startSlot: slot.index,
        endSlot: slot.index + 1,
        status: slot.status,
      };
    } else if (currentBlock.status === slot.status && currentBlock.endSlot === slot.index) {
      // Extend the current block
      currentBlock.endSlot = slot.index + 1;
    } else {
      // Different status or gap - push the current block and start a new one
      blocks.push(currentBlock);
      currentBlock = {
        startSlot: slot.index,
        endSlot: slot.index + 1,
        status: slot.status,
      };
    }
  }

  if (currentBlock) {
    blocks.push(currentBlock);
  }

  return blocks;
}

export default function ScheduleSummaryPage({
  availability,
  hourlyRate,
  companyName,
  companyLogo,
  location,
  onWatchVideo,
  onBack,
  onConfirm,
}: ScheduleSummaryPageProps) {
  // Calculate hours
  let preferredHours = 0;
  let availableHours = 0;

  availability.forEach(day => {
    day.slots.forEach(slot => {
      if (slot.status === SLOT_STATUS.PREFERRED) {
        preferredHours += 0.5; // Each slot is 30 minutes
      } else if (slot.status === SLOT_STATUS.AVAILABLE) {
        availableHours += 0.5;
      }
    });
  });

  const totalHours = preferredHours + availableHours;
  const weeklyPay = Math.round(totalHours * hourlyRate);

  // Day abbreviations matching Figma
  const dayAbbreviations = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* SECTION A: HEADER (reused from other screens) */}
      <div className="flex-shrink-0 bg-white px-5 pt-[14px]">
        {/* Logo, Company Name, Location, Watch Video */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-2">
            {/* Company Logo */}
            <CompanyLogo
              src={companyLogo}
              alt={companyName}
              className="w-[57px] h-[57px] rounded"
            />
            
            {/* Company Info */}
            <div className="flex flex-col justify-center">
              <h1 className="text-[19px] font-bold text-[#28292d]">
                {companyName}
              </h1>
              <p className="text-[17px] font-normal text-[#28292d]">
                {location}
              </p>
            </div>
          </div>

          {/* Watch Video Link */}
          <button
            onClick={onWatchVideo}
            className="flex items-center gap-1 self-center"
          >
            <svg
              className="w-3 h-3"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2 1.5L10 6L2 10.5V1.5Z"
                fill="#0156FF"
                stroke="#0156FF"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-[16px] font-normal text-[#0156FF]">
              Watch Video
            </span>
          </button>
        </div>

        {/* Gray Divider */}
        <div className="h-px bg-[#b9babf] mx-4" />
      </div>

      {/* SECTION B: SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-20">
        {/* Estimated Weekly Pay */}
        <div className="mb-4">
          <p className="text-[14px] font-normal text-[#8e8e8f] mb-1">
            Estimated Weekly Pay (at ${hourlyRate}/hr)
          </p>
          <p className="text-[34px] font-bold text-[#12b71a]">
            ${weeklyPay}
          </p>
        </div>

        {/* Hours Summary */}
        <div className="flex items-center gap-2 text-[14px]" style={{ marginBottom: '19px' }}>
          <span className="font-bold text-[#12b71a]">{preferredHours.toFixed(1)}h</span>
          <span className="font-normal text-[#28292d]">Preferred</span>
          <span className="font-bold text-[#28292d]">•</span>
          <span className="font-bold text-[#2e98db]">{availableHours.toFixed(1)}h</span>
          <span className="font-normal text-[#28292d]">Available</span>
          <span className="font-bold text-[#28292d]">•</span>
          <span className="font-bold text-[#28292d]">{totalHours.toFixed(1)}h</span>
          <span className="font-normal text-[#28292d]">Total</span>
        </div>

        {/* Weekly Schedule Heading */}
        <h2 className="text-[16px] font-bold text-[#28292d] mb-3">
          Weekly Schedule
        </h2>

        {/* Horizontal line below heading */}
        <div className="h-px bg-[#eeeeee] mb-4" />

        {/* Day-by-Day Schedule */}
        <div className="space-y-4">
          {availability.map((day, dayIndex) => {
            const timeBlocks = getTimeBlocks(day.slots);
            const dayAbbr = dayAbbreviations[dayIndex];

            return (
              <div key={day.dayOfWeek} className="border-b border-[#eeeeee] pb-4">
                <div className="flex justify-between items-start">
                  <p className="text-[16px] font-bold text-[#28292d] min-w-[60px]">
                    {dayAbbr}
                  </p>
                  <div className="flex-1 text-right">
                    {timeBlocks.length === 0 ? (
                      <p className="text-[16px] font-normal text-[#8e8e8f]">
                        None selected
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {timeBlocks.map((block, blockIndex) => {
                          const startTime = slotIndexToTimeString(block.startSlot);
                          const endTime = slotIndexToTimeString(block.endSlot);
                          const color = block.status === SLOT_STATUS.PREFERRED
                            ? '#12b71a'
                            : '#2e98db';

                          return (
                            <p
                              key={blockIndex}
                              className="text-[16px] font-bold"
                              style={{ color }}
                            >
                              {startTime} - {endTime}
                            </p>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Horizontal line below last day */}
        <div className="h-px bg-[#eeeeee] mt-4" />
      </div>

      {/* SECTION C: FIXED BOTTOM BUTTONS */}
      <div className="fixed bottom-0 left-0 right-0 px-5 py-4 flex items-center justify-between bg-white border-t border-[#eeeeee] z-50">
        <button
          onClick={onBack}
          className="bg-[#8E8E8F] text-white text-[18px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
          style={{ width: '90px', height: '40px' }}
        >
          Back
        </button>
        <button
          onClick={onConfirm}
          className="bg-[#0156FF] text-white text-[18px] font-bold rounded-full active:scale-[0.98] transition-transform flex items-center justify-center"
          style={{ width: '90px', height: '40px' }}
        >
          Confirm
        </button>
      </div>
    </div>
  );
}

